import React, { useRef, useState, useLayoutEffect } from 'react';
import _debounce from 'lodash/debounce';
import _merge from 'lodash/merge';
import ChevronLeftIcon from '@nokia-csf-uxr/ccfk-assets/ChevronLeftIcon';
import ChevronRightIcon from '@nokia-csf-uxr/ccfk-assets/ChevronRightIcon';
import { useSpring } from 'react-spring';
import { SPEED_FAST } from '@nokia-csf-uxr/freeform-design-tokens/tokens/speed';
import { TRANSITION_ACCELERATE } from '@nokia-csf-uxr/freeform-design-tokens/tokens/transition';
import { SPACING_12 } from '@nokia-csf-uxr/freeform-design-tokens/tokens/spacing';
import List from 'components/List';
import IconButton from 'components/IconButton';
import { getContext, getEasing, getDuration } from 'common';

import Styled from './styles';

const ELEVATION_CONFIG = {
  elevationIndex: 1 as const,
  dark: true
};

const DEFAULT_LIST_STYLE = (isHorizontal, background) => ({
  padding: isHorizontal ? '0' : `${SPACING_12} 0 0 0`,
  overflow: isHorizontal ? 'hidden' : undefined,
  backgroundColor: background,
});

const SCROLL_BUTTON_WIDTH = 24; // small IconButton + padding

export interface StepFormContextInterface {
  isHorizontal?: boolean;
  variant?: 'compact' | 'default';
  hoverEffect?: boolean;
  setSelectedRef?(...args: unknown[]): unknown;
}

const StepFormContext = React.createContext<StepFormContextInterface | null>({});

export interface StepFormProps {
  /** The orientation of the `StepForm`. */
  orientation?: 'vertical' | 'horizontal';
  /** The variant for styling purposes.  Applies to orientation="vertical". 'compact' variants do not use the StepLine or StepDetails. */
  variant?: 'compact' | 'default';
  /** Controls presence of hover and focus effects on the steps. */
  hoverEffect?: boolean;
  /** The Icon to use for the left side scroll button. Overrides  <ChevronLeftIcon />. Applies to orientation="horizontal". */
  leftScrollIcon?: React.ReactNode;
  /** The Icon to use for the right side scroll button. Overrides  <ChevronRightIcon />. Applies to orientation="horizontal". */
  rightScrollIcon?: React.ReactNode;
  /** The content of the `<StepForm />`, consider using `Step`, `StepParent`, `HorizontalStep`. */
  children: React.ReactNode;
  /** Properties to apply to the InnerSteps element.  */
  innerStepProps?: {};
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** @ignore */
  style?: React.CSSProperties
}
/** `StepForm`, all other props will be provided to the List component. */
const StepForm = React.forwardRef<HTMLElement, StepFormProps>((props: StepFormProps, ref) => getContext(({ RTL, theme }) => {
  const {
    children,
    orientation,
    variant,
    hoverEffect,
    leftScrollIcon,
    rightScrollIcon,
    theme: themeProp,
    innerStepProps,
    ...otherProps
  } = props;
  const { style: listPropsStyle, ...otherListProps } = otherProps;
  const containerRef = useRef(null);
  const [isScrollable, setIsScrollable] = useState<undefined | { canScrollLeft?: boolean; canScrollRight?: boolean }>(undefined);
  const [, setX] = useSpring(() => ({ x: 0 }));
  const childrenRef = useRef(null);

  const resolvedTheme = _merge({}, theme.STEP_FORM, themeProp.STEP_FORM);
  const isHorizontal = orientation === 'horizontal';
  const isScrollableLeft = isScrollable && isScrollable.canScrollLeft;
  const isScrollableRight = isScrollable && isScrollable.canScrollRight;

  const updateScrollable = _debounce(() => {
    if (!childrenRef.current) return;
    const { scrollWidth, clientWidth, scrollLeft } = childrenRef.current;
    /** Legacy Edge Browser interprets scrollLeft differently to chrome. */
    const canScrollLeft = () => {
      if (RTL) {
        return Math.abs(scrollLeft) !== scrollWidth - clientWidth;
      }
      return scrollLeft > 0;
    };
    const canScrollRight = () => {
      if (RTL) {
        return scrollLeft < 0;
      }
      return Math.abs(scrollLeft) !== scrollWidth - clientWidth;
    };
    setIsScrollable({ canScrollLeft: canScrollLeft(), canScrollRight: canScrollRight() });
  }, 100);

  const scrollTo = direction => () => {
    if (!childrenRef.current) return;
    const element = childrenRef && childrenRef.current;
    const scrollDistance = childrenRef.current.clientWidth * 0.75;
    const totalScrollDistance =
      (direction === 'right' ? -scrollDistance : scrollDistance) - SCROLL_BUTTON_WIDTH;
    element &&
      setX({
        config: { duration: getDuration(SPEED_FAST), easing: getEasing(TRANSITION_ACCELERATE) },
        x: element.scrollLeft + totalScrollDistance,
        // @ts-expect-error: This might be fixed after updating react-spring
        onFrame: ({ x }) => {
          element.scroll(x, 0);
        }
      });
  };

  const scrollToStep = (step) => {
    if (!childrenRef.current) return;
    const element = childrenRef && childrenRef.current;
    const stepElement = step;
    if (!stepElement) return;

    const { clientWidth } = element;
    const { width: stepWidth, right: sRight, left: sLeft } = stepElement.getBoundingClientRect();
    const sWidth = RTL ? clientWidth : isScrollableRight ? (clientWidth - SCROLL_BUTTON_WIDTH) : clientWidth;
    const shouldScroll = () => RTL ? (sWidth < sRight) || sRight < 0 : (sWidth < sLeft) || sLeft < 0;
    if (!shouldScroll()) return;
    const scrollDistance = RTL ? -containerRef.current.clientWidth : containerRef.current.clientWidth;
    const totalScrollDistance = RTL ? (sLeft + scrollDistance) + stepWidth : (sRight - scrollDistance) + stepWidth;
    element &&
      setX({
        config: { duration: getDuration(SPEED_FAST), easing: getEasing(TRANSITION_ACCELERATE) },
        x: element.scrollLeft + totalScrollDistance,
        // @ts-expect-error: This might be fixed after updating react-spring
        onFrame: ({ x }) => {
          element.scroll(x, 0);
        }
      });
  };

  // Scroll to selected step, step is a ref
  const scrollToSelected = (step) => {
    if (!step || !(document && document.scrollingElement)) return;
    scrollToStep(step);
  };

  // Set selected step, and scroll to it if needed. step is a ref
  const setSelectedRefFunc = (step) => {
    scrollToSelected(step);
  };

  useLayoutEffect(() => {
    updateScrollable();
    childrenRef.current.addEventListener('scroll', updateScrollable);
    window.addEventListener('resize', updateScrollable);
    return () => {
      childrenRef.current.removeEventListener('scroll', updateScrollable);
      window.removeEventListener('resize', updateScrollable);
    };
  }, [RTL, ref]);

  const renderLeftButton = () => {
    if (isScrollableLeft) {
      const leftIcon = RTL ? <ChevronRightIcon /> : <ChevronLeftIcon />;
      const icon = leftScrollIcon || leftIcon;
      return (
        <Styled.ScrollButtonContainer left $theme={resolvedTheme}>
          <IconButton
            size="small"
            onClick={scrollTo('right')}
          >
            {icon}
          </IconButton>
        </Styled.ScrollButtonContainer>
      );
    }
    return null;
  };

  const renderRightButton = () => {
    if (isScrollableRight) {
      const rightIcon = RTL ? <ChevronLeftIcon /> : <ChevronRightIcon />;
      const icon = rightScrollIcon || rightIcon;
      return (
        <Styled.ScrollButtonContainer right $theme={resolvedTheme}>
          <IconButton
            size="small"
            onClick={scrollTo('left')}
          >
            {icon}
          </IconButton>
        </Styled.ScrollButtonContainer>
      );
    }
    return null;
  };

  return (
    <List
      ref={ref}
      style={{
        ...DEFAULT_LIST_STYLE(isHorizontal, resolvedTheme.background),
        ...listPropsStyle,
      }}
      elevationProps={{ ...ELEVATION_CONFIG }}
      ulProps={{ role: 'presentation', style: { display: 'block' } }}
      onFocus={(e) => { e.preventDefault(); e.stopPropagation(); }}
      hoverEffect={hoverEffect}
      {...otherListProps}
    >
      <StepFormContext.Provider value={{ isHorizontal, variant, hoverEffect, setSelectedRef: setSelectedRefFunc }}>
        <Styled.ScrollContainer ref={containerRef} $rtl={RTL} $isHorizontal={isHorizontal} >
          {isHorizontal && renderLeftButton()}
          <Styled.InnerSteps role="group" ref={childrenRef} $isHorizontal={isHorizontal} {...innerStepProps} >
            {children}
          </Styled.InnerSteps>
          {isHorizontal && renderRightButton()}
        </Styled.ScrollContainer>
      </StepFormContext.Provider>
    </List>
  );
}));

StepForm.defaultProps = {
  orientation: 'vertical',
  variant: 'default',
  hoverEffect: true,
  leftScrollIcon: undefined,
  rightScrollIcon: undefined,
  innerStepProps: undefined,
  theme: {},
  style: {}
};

StepForm.displayName = 'StepForm';

export { StepForm as default, StepFormContext };
